import { Draggable } from "@hello-pangea/dnd"
import React, { useEffect, useRef, useState } from "react"

import { trpc } from "../lib/trpc"
import AssigneeSelector from "./AssigneeSelector"
import PrioritySelector from "./PrioritySelector"
import ProjectSelector from "./ProjectSelector"
import WeeklyPrioritySelector from "./WeeklyPrioritySelector"

interface TaskLabel {
  id: string | null
  name: string | null
  color: string | null
  description?: string | null
}

interface Task {
  id: string
  content: string
  description?: string | null
  projectId: string
  assigneeId?: string | null
  status: "todo" | "in_progress" | "done"
  priority: number
  estimatedMinutes?: number | null
  aiCategorized: boolean
  isWeeklyPriority?: boolean
  weekAssigned?: string | null
  createdAt: Date
  updatedAt: Date
  // New fields for hierarchy display
  projectTitle?: string
  projectDescription?: string
  // Assignee info
  assigneeName?: string | null
  assigneeEmail?: string | null
  assigneeAvatarUrl?: string | null
  // Labels
  labels?: TaskLabel[]
}

interface Project {
  id: string
  title: string
  description?: string
  strategyContent?: string
  parentId?: string
  priority: number
  userId: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  children: Project[]
  taskCount: number
}

interface TaskCardProps {
  task: Task
  index: number
  selectedTaskIds: Set<string>
  projects: Project[]
  draggableId?: string
  isDragDisabled?: boolean
  isFromDifferentProject?: boolean
  taskBreadcrumbs?: { id: string; title: string }[]

  // Event handlers
  onTaskClick: (taskId: string, event: React.MouseEvent) => void
  onTaskSelection: (
    taskId: string,
    isSelected: boolean,
    event?: React.ChangeEvent<HTMLInputElement>,
    taskIndex?: number,
  ) => void
  onEditTaskDescription: (taskId: string) => void
  onArchiveTask: (taskId: string) => void
  onProjectSelect: (projectId: string) => void
  onProjectChange: (taskId: string, newProjectId: string) => void
  onAssigneeChange: (taskId: string, assigneeId: string | null) => void
  onStatusChange: (taskId: string, status: "todo" | "in_progress" | "done") => void
  onWeeklyPriorityToggle: (taskId: string, isWeeklyPriority: boolean, weekStart?: Date) => void
  onPriorityChange: (taskId: string, priority: number) => void
  onEstimatedTimeChange: (taskId: string, estimatedMinutes: number | null) => void
  onLabelsChange: (taskId: string, labelIds: string[]) => void
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  index,
  selectedTaskIds,
  projects,
  draggableId = `task-${task.id}`,
  isDragDisabled = false,
  isFromDifferentProject = false,
  taskBreadcrumbs = [],
  onTaskClick,
  onTaskSelection,
  onEditTaskDescription,
  onArchiveTask,
  onProjectSelect,
  onProjectChange,
  onAssigneeChange,
  onStatusChange,
  onWeeklyPriorityToggle,
  onPriorityChange,
  onEstimatedTimeChange,
  onLabelsChange,
}) => {
  const [showTimeEditor, setShowTimeEditor] = useState(false)
  const [showLabelsEditor, setShowLabelsEditor] = useState(false)
  const [tempEstimatedTime, setTempEstimatedTime] = useState(task.estimatedMinutes || 0)
  const [optimisticLabels, setOptimisticLabels] = useState<TaskLabel[]>(task.labels || [])
  const timeEditorRef = useRef<HTMLDivElement>(null)
  const labelsEditorRef = useRef<HTMLDivElement>(null)

  // Get available labels
  const { data: availableLabels } = trpc.brain.getTaskLabels.useQuery()

  // Update optimistic labels when task labels change
  useEffect(() => {
    setOptimisticLabels(task.labels || [])
  }, [task.labels])

  // Update estimated time mutation
  const updateEstimatedTimeMutation = trpc.brain.updateTaskEstimatedTime.useMutation({
    onSuccess: () => {
      setShowTimeEditor(false)
    },
  })

  // Labels mutations
  const assignLabelMutation = trpc.brain.assignTaskLabel.useMutation({
    onSuccess: (_, variables) => {
      // Find the label that was added
      if (variables && variables.labelId) {
        const addedLabel = availableLabels?.labels?.find((l) => l.id === variables.labelId)
        if (addedLabel) {
          setOptimisticLabels((prev) => [...prev, addedLabel])
        }
      }
    },
  })
  const removeLabelMutation = trpc.brain.removeTaskLabel.useMutation({
    onSuccess: (_, variables) => {
      if (variables && variables.labelId) {
        setOptimisticLabels((prev) => prev.filter((l) => l.id !== variables.labelId))
      }
    },
  })

  // Close popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeEditorRef.current && !timeEditorRef.current.contains(event.target as Node)) {
        setShowTimeEditor(false)
      }
      if (labelsEditorRef.current && !labelsEditorRef.current.contains(event.target as Node)) {
        setShowLabelsEditor(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const statusColors = {
    todo: "bg-foregroundx text-primary",
    in_progress: "bg-yellow-100 text-yellow-700",
    done: "bg-green-100 text-green-700",
  }

  // Helper function to format time
  const formatEstimatedTime = (minutes?: number | null) => {
    if (!minutes) return null
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  const handleSaveEstimatedTime = () => {
    const newTime = tempEstimatedTime > 0 ? tempEstimatedTime : null
    onEstimatedTimeChange(task.id, newTime)
    updateEstimatedTimeMutation.mutate({
      taskId: task.id,
      estimatedMinutes: newTime,
    })
  }

  const handleToggleLabel = (labelId: string) => {
    const currentLabelIds = optimisticLabels.map((l) => l.id).filter((id): id is string => id !== null)
    const hasLabel = currentLabelIds.includes(labelId)

    if (hasLabel) {
      // Optimistically update
      setOptimisticLabels((prev) => prev.filter((l) => l.id !== labelId))
      removeLabelMutation.mutate({
        taskId: task.id,
        labelId,
      })
      // Update parent
      const newLabelIds = currentLabelIds.filter((id) => id !== labelId)
      onLabelsChange(task.id, newLabelIds)
    } else {
      // Find the label to add
      const labelToAdd = availableLabels?.labels?.find((l) => l.id === labelId)
      if (labelToAdd) {
        // Optimistically update
        setOptimisticLabels((prev) => [...prev, labelToAdd])
        assignLabelMutation.mutate({
          taskId: task.id,
          labelId,
        })
        // Update parent
        const newLabelIds = [...currentLabelIds, labelId]
        onLabelsChange(task.id, newLabelIds)
      }
    }
  }

  const estimatedTime = formatEstimatedTime(task.estimatedMinutes)

  return (
    <Draggable key={draggableId} draggableId={draggableId} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={(e) => onTaskClick(task.id, e)}
          className={`p-3 border border-border rounded-lg transition-all cursor-pointer select-none ${
            snapshot.isDragging ? "shadow-lg bg-blue-50 border-blue-300" : "bg-background "
          } ${selectedTaskIds.has(task.id) ? "ring-2 ring-blue-500 bg-blue-50" : ""}`}
          style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none" }}
        >
          <div className="flex items-start gap-2">
            {/* Task Selection Checkbox */}
            <input
              type="checkbox"
              checked={selectedTaskIds.has(task.id)}
              onChange={(e) => onTaskSelection(task.id, e.target.checked, e, index)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 rounded border-gray-300  focus:ring-blue-500"
            />

            {/* Drag Handle */}
            {!isDragDisabled && (
              <div
                {...provided.dragHandleProps}
                className="flex flex-col gap-0.5 mt-0.5 cursor-grab active:cursor-grabbing"
              >
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              </div>
            )}

            <div className="flex-1">
              {/* Project hierarchy breadcrumbs with selector */}
              {taskBreadcrumbs.length > 0 && (
                <div className="mb-1">
                  <ProjectSelector
                    currentProjectId={task.projectId}
                    taskId={task.id}
                    taskBreadcrumbs={taskBreadcrumbs}
                    isFromDifferentProject={isFromDifferentProject}
                    onProjectChange={onProjectChange}
                    onProjectSelect={onProjectSelect}
                  />
                </div>
              )}

              {/* Task content */}
              <p className=" font-medium text-sm leading-tight">{task.content}</p>

              {/* Labels */}
              {optimisticLabels && optimisticLabels.length > 0 && (
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {optimisticLabels.map((label) => (
                    <button
                      key={label.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowLabelsEditor(true)
                      }}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded hover:opacity-80 transition-opacity cursor-pointer"
                      style={{
                        backgroundColor: label.color ? `${label.color}20` : "#f3f4f620",
                        color: label.color || "#6b7280",
                      }}
                      title="Click to manage labels"
                    >
                      {label.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Task badges */}
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {/* Priority Badge */}
                <PrioritySelector
                  taskId={task.id}
                  currentPriority={task.priority}
                  onPriorityChange={onPriorityChange}
                />

                {/* Assignee Selector */}
                <AssigneeSelector
                  currentAssignee={{
                    id: task.assigneeId || null,
                    name: task.assigneeName || null,
                    email: task.assigneeEmail || null,
                    avatarUrl: task.assigneeAvatarUrl || null,
                  }}
                  taskId={task.id}
                  onAssigneeChange={onAssigneeChange}
                />

                {/* Estimated Time - Clickable */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setTempEstimatedTime(task.estimatedMinutes || 0)
                      setShowTimeEditor(!showTimeEditor)
                    }}
                    className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded transition-colors ${
                      estimatedTime
                        ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        : "bg-foregroundx text-gray-500 "
                    }`}
                  >
                    ⏱️ {estimatedTime || "No estimate"}
                  </button>

                  {/* Time Editor Popover */}
                  {showTimeEditor && (
                    <div
                      ref={timeEditorRef}
                      className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 min-w-[200px]"
                    >
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-primary">Estimated time (minutes)</label>
                        <input
                          type="number"
                          value={tempEstimatedTime}
                          onChange={(e) => setTempEstimatedTime(parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0"
                          min="0"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEstimatedTime}
                            disabled={updateEstimatedTimeMutation.isLoading}
                            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-blue-400"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setShowTimeEditor(false)}
                            className="px-2 py-1 bg-gray-300 text-primary text-xs rounded hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Weekly Priority Selector */}
                <WeeklyPrioritySelector
                  taskId={task.id}
                  currentWeekAssigned={task.weekAssigned}
                  isWeeklyPriority={task.isWeeklyPriority}
                  onWeeklyPriorityChange={onWeeklyPriorityToggle}
                />

                {/* AI Categorized */}
                {task.aiCategorized && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                    🤖
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 ml-2">
              {/* Labels Button */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowLabelsEditor(!showLabelsEditor)
                  }}
                  className="text-gray-400 hover: p-0.5 text-sm"
                  title="Manage labels"
                >
                  🏷️
                </button>

                {/* Labels Editor Popover */}
                {showLabelsEditor && (
                  <div
                    ref={labelsEditorRef}
                    className="absolute top-full right-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 min-w-[250px]"
                  >
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-primary mb-2">Task Labels</div>
                      {availableLabels && availableLabels.labels && availableLabels.labels.length > 0 ? (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {availableLabels.labels.map((label) => {
                            const isAssigned = optimisticLabels.some((l) => l.id === label.id)
                            return (
                              <label key={label.id} className="flex items-center gap-2 p-1  rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isAssigned}
                                  onChange={() => handleToggleLabel(label.id)}
                                  className="rounded border-gray-300  focus:ring-blue-500"
                                />
                                <span
                                  className="inline-flex items-center px-2 py-1 text-xs rounded"
                                  style={{
                                    backgroundColor: `${label.color}20`,
                                    color: label.color,
                                  }}
                                >
                                  {label.name}
                                </span>
                              </label>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500">No labels available</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Edit Description Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onEditTaskDescription(task.id)
                }}
                className="text-gray-400 hover: p-0.5 text-sm"
                title="Edit task description"
              >
                📝
              </button>

              {/* Status Dropdown */}
              <select
                value={task.status}
                onChange={(e) => {
                  e.stopPropagation()
                  onStatusChange(task.id, e.target.value as "todo" | "in_progress" | "done")
                }}
                onClick={(e) => e.stopPropagation()}
                className={`px-1.5 py-0.5 text-xs rounded border-0 ${statusColors[task.status]}`}
              >
                <option value="todo">Todo</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>

              {/* Archive Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onArchiveTask(task.id)
                }}
                className="text-gray-400 hover:text-destructive p-0.5 text-sm"
                title="Archive task"
              >
                🗃️
              </button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  )
}

export default TaskCard
